import React from 'react';
import dayjs from 'dayjs';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { initialState as customerInitialState } from '~redux/slices/customer';
import { TGetCustomerListingFilters } from '~types/feat/customer';

import SearchAndFilterBar, { TSearchAndFilterBarProps } from '~components/molecules/SearchAndFilterBar';

import { CustomerAccStatusEnum } from '~utils/enums';
import * as Styles from './_customerManagementSearchAndFilterBar.module.scss';

const CustomerManagementSearchAndFilterBar = () => {
    const filters = useAppSelector(state => state.customer.customerListing.filters);
    const initialFilters = customerInitialState.customerListing.filters;

    const branchesList = useAppSelector(state => state.branch.allBranches);

    const dispatch = useAppDispatch();

    const setFilters = (inputFilters: TGetCustomerListingFilters) => dispatch(combinedActions.customerSetGetCustomerListingFilters(inputFilters));
    const handleSearchClick = () => dispatch(combinedActions.customerGetCustomerListingAttempt());

    const handleCancelClick = () => {
        setFilters(initialFilters);
        dispatch(combinedActions.customerGetCustomerListingAttempt());
    };

    const massagedBranchesListOption = () => {
        if (!branchesList.length) return [];

        return branchesList.map((branch) => ({
            label: branch.branchName,
            value: branch.branchCode,
        }));
    };

    const fields: TSearchAndFilterBarProps['fields'] = [
        {
            label: 'Customer Name:',
            type: 'input' as const,
            inputParams: {
                value: filters.name,
                setValue: (event) => setFilters({ ...filters, name: event.target.value }),
                onKeyDown: (event) => { if (event.key === 'Enter') handleSearchClick(); },
                className: Styles.input,
            },
        },
        {
            label: 'ID No.:',
            type: 'input' as const,
            inputParams: {
                value: filters.idNo,
                setValue: (event) => setFilters({ ...filters, idNo: event.target.value }),
                onKeyDown: (event) => { if (event.key === 'Enter') handleSearchClick(); },
                className: Styles.input,
            },
        },
        {
            label: 'Branch:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: massagedBranchesListOption(),
                value: filters.branchName || '',
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({
                    ...filters,
                    branchName: event.target.value,
                }),
                optionSelected: !!branchesList.find((branch) => branch.branchName === filters.branchName),
                noOptionsMessage: 'No branches found',
                className: Styles.input,
            },
        },
        {
            label: 'Status:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: [
                    {
                        label: 'Active',
                        value: CustomerAccStatusEnum.Active,
                    },
                    {
                        label: 'Deactivated',
                        value: CustomerAccStatusEnum.Deactivated,
                    },
                ],
                value: filters.status,
                setValue: (event) => setFilters({ ...filters, status: event.target.value }),
                optionSelected: !!CustomerAccStatusEnum[filters.status as keyof typeof CustomerAccStatusEnum],
                noOptionsMessage: 'No status found',
                className: Styles.input,
            },
        },
        {
            label: 'Date From:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateFrom,
                setDate: (date: string) => {
                    if (date > filters?.dateTo) {
                        setFilters({ ...filters, dateFrom: date, dateTo: '' });
                    } else setFilters({ ...filters, dateFrom: date });
                },
                maxDate: dayjs().toISOString(),
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                selectsStart: true,
            },
        },
        {
            label: 'Date To:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateTo,
                minDate: filters.dateFrom,
                setDate: (date: string) => setFilters({ ...filters, dateTo: date }),
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                selectsEnd: true,
            },
        },
    ];

    return (
        <SearchAndFilterBar
            fields={fields}
            searchOnClick={handleSearchClick}
            cancelOnClick={handleCancelClick}
            containerClassName={Styles['content-container']}
        />
    );
};

export default CustomerManagementSearchAndFilterBar;
